import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MedalIcon, TrendingUpIcon, HeartIcon, CalendarIcon, ZapIcon, AwardIcon, ShieldCheckIcon, UsersIcon, HeartHandshakeIcon, CheckCircleIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { SectionHeader } from '../components/SectionHeader';
import { ValuesSection } from '../components/ValuesSection';
const inputStyles = 'w-full px-4 py-3 rounded-xl border border-navy-200 focus:ring-2 focus:ring-gold-500 focus:border-gold-500 outline-none transition-all bg-navy-50/50 focus:bg-white';
const labelStyles = 'block text-sm font-bold text-navy-900 mb-2';
function CoachApplicationForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTimeout(() => {
      setIsSubmitted(true);
    }, 500);
  };
  if (isSubmitted) {
    return <div className="bg-white p-8 rounded-2xl shadow-sm border border-navy-50 text-center h-full flex flex-col items-center justify-center min-h-[400px]" role="status" aria-live="polite" data-id="element-564">
        <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mb-6" data-id="element-565">
          <CheckCircleIcon className="w-8 h-8" data-id="element-566" />
        </div>
        <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-567">
          Application Sent!
        </h3>
        <p className="text-navy-600 mb-8" data-id="element-568">
          Thank you for your interest in coaching with us. A member of our team
          will be in touch shortly.
        </p>
        <Button variant="secondary" onClick={() => setIsSubmitted(false)} data-id="element-569">
          Submit Another Application
        </Button>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-navy-50" aria-label="Coach application form" data-id="element-570">
      <h3 className="text-xl font-bold text-navy-900 mb-6" data-id="element-571">
        Apply to Coach With Us
      </h3>
      <div className="space-y-5" data-id="element-572">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-573">
          <div data-id="element-574">
            <label htmlFor="coach-firstName" className={labelStyles} data-id="element-575">
              First Name
            </label>
            <input type="text" id="coach-firstName" required aria-required="true" className={inputStyles} placeholder="John" data-id="element-576" />
          </div>
          <div data-id="element-577">
            <label htmlFor="coach-lastName" className={labelStyles} data-id="element-578">
              Last Name
            </label>
            <input type="text" id="coach-lastName" required aria-required="true" className={inputStyles} placeholder="Smith" data-id="element-579" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-580">
          <div data-id="element-581">
            <label htmlFor="coach-email" className={labelStyles} data-id="element-582">
              Email Address
            </label>
            <input type="email" id="coach-email" required aria-required="true" className={inputStyles} placeholder="john@example.com" data-id="element-583" />
          </div>
          <div data-id="element-584">
            <label htmlFor="coach-phone" className={labelStyles} data-id="element-585">
              Phone Number
            </label>
            <input type="tel" id="coach-phone" className={inputStyles} placeholder="07700 900000" data-id="element-586" />
          </div>
        </div>

        <div data-id="element-587">
          <label htmlFor="coach-sport" className={labelStyles} data-id="element-588">
            Which sport would you like to coach?
          </label>
          <select id="coach-sport" required aria-required="true" className={`${inputStyles} appearance-none`} data-id="element-589">
            <option value="" data-id="element-590">Select a sport</option>
            <option value="badminton" data-id="element-591">Badminton</option>
            <option value="multi-sports" data-id="element-592">Multi-Sports</option>
            <option value="netball" data-id="element-593">Netball</option>
            <option value="multiple" data-id="element-594">Multiple Sports</option>
          </select>
        </div>

        <div data-id="element-595">
          <label htmlFor="coach-location" className={labelStyles} data-id="element-596">
            Preferred coaching location
          </label>
          <select id="coach-location" required aria-required="true" className={`${inputStyles} appearance-none`} data-id="element-597">
            <option value="" data-id="element-598">Select a region</option>
            <option value="derbyshire" data-id="element-599">Derbyshire</option>
            <option value="hampshire" data-id="element-600">Hampshire</option>
            <option value="hertfordshire" data-id="element-601">Hertfordshire</option>
            <option value="flexible" data-id="element-602">Flexible / Multiple Regions</option>
          </select>
        </div>

        <div data-id="element-603">
          <label htmlFor="coach-qualifications" className={labelStyles} data-id="element-604">
            Coaching qualifications
          </label>
          <select id="coach-qualifications" required aria-required="true" className={`${inputStyles} appearance-none`} data-id="element-605">
            <option value="" data-id="element-606">Select your level</option>
            <option value="none" data-id="element-607">No formal qualifications yet</option>
            <option value="level1" data-id="element-608">Level 1 Coaching Award</option>
            <option value="level2" data-id="element-609">Level 2 Coaching Award</option>
            <option value="level3" data-id="element-610">Level 3+ / Advanced</option>
            <option value="degree" data-id="element-611">Sports Science / PE Degree</option>
            <option value="other" data-id="element-612">Other</option>
          </select>
        </div>

        <div data-id="element-613">
          <label htmlFor="coach-experience" className={labelStyles} data-id="element-614">
            Tell us about your experience
          </label>
          <textarea id="coach-experience" rows={4} required aria-required="true" className={`${inputStyles} resize-none`} placeholder="Tell us about your coaching background, experience with young people, and why you'd like to join Bright Star Sports..." data-id="element-615"></textarea>
        </div>

        <Button type="submit" variant="primary" className="w-full" data-id="element-616">
          Submit Application
        </Button>
      </div>
    </form>;
}
export function BecomeACoachPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="bg-white pt-20" data-id="element-617">
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 min-h-[320px] md:min-h-[400px] overflow-hidden bg-brand-blue" data-id="element-618">
        <img src="https://cdn.magicpatterns.com/uploads/pTRbE2fvqTGzPDd7dvdT5h/Group1.webp" alt="" className="absolute inset-0 w-full h-full object-cover" aria-hidden="true" data-id="element-619" />
        <div className="absolute inset-0 bg-brand-blue/85" data-id="element-620"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-621">
          <div className="mb-8" data-id="element-622">
            <nav className="flex text-sm font-medium text-white/70 mb-8" data-id="element-623">
              <Link to="/" className="hover:text-white transition-colors" data-id="element-624">
                Home
              </Link>
              <span className="mx-2" data-id="element-625">/</span>
              <span className="text-white" data-id="element-626">Become a Coach</span>
            </nav>
          </div>

          <div className="max-w-3xl" data-id="element-627">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-628">
              <p className="text-sm md:text-base tracking-[0.3em] uppercase text-white/70 mb-4" data-id="element-629">
                TEAM UP WITH OLYMPIANS
              </p>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-6 leading-tight" data-id="element-630">
                Be a Part of Us
              </h1>
              <p className="text-xl text-white/90 mb-10 max-w-2xl leading-relaxed" data-id="element-631">
                Join our team of Olympian coaches and help inspire the next
                generation of young athletes across the UK.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-632">
                <Button variant="primary" size="lg" href="#apply" data-id="element-633">
                  Apply Now
                </Button>
                <Button variant="secondary" size="lg" href="/#contact" className="border-white text-white hover:bg-white/20" data-id="element-634">
                  Contact Us
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Purpose Section */}
      <section className="py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-635">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-636">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-navy-50 rounded-2xl aspect-[4/3] flex items-center justify-center overflow-hidden border border-navy-100" data-id="element-637">
            <img src="https://cdn.magicpatterns.com/uploads/ecKEmB1atgehL18yZw2WWr/Athletics-Arlo-Aminaya-MS-Photo.jpg" alt="Young children enjoying a Bright Star Sports session" className="w-full h-full object-cover" data-id="element-638" />
          </motion.div>

          <div data-id="element-639">
            <SectionHeader eyebrow="OUR PURPOSE" title="Inspiring the Next Generation" align="left" data-id="element-640" />
            <h3 className="text-xl font-bold text-navy-900 mb-4" data-id="element-641">
              Why Join Us as a Coach?
            </h3>
            <div className="space-y-6 text-lg text-navy-600" data-id="element-642">
              <p className="leading-relaxed" data-id="element-643">
                As a coach with Bright Star Sports, you'll have the unique
                opportunity to make a lasting impact on the lives of young
                athletes. You'll be working with a team of experienced coaches
                who share your passion for sports and your commitment to
                excellence. Our programmes are scientifically designed with
                Olympians and sports specialists to develop not just better
                athletes, but confident, resilient young people.
              </p>
              <p className="leading-relaxed" data-id="element-644">
                Whether you're an experienced coach looking for a new challenge
                or a passionate sports enthusiast ready to start your coaching
                journey, we want to hear from you.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer Section */}
      <section className="py-16 md:py-20 bg-navy-50 border-t border-gray-100" data-id="element-645">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-646">
          <div className="text-center mb-12" data-id="element-647">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-4" data-id="element-648">
              What We Offer
            </h2>
            <h3 className="text-xl md:text-2xl font-bold text-brand-blue uppercase" data-id="element-649">
              Benefits of Coaching With Us
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-650">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-651">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-652">
                <MedalIcon className="w-7 h-7" data-id="element-653" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-654">
                Olympian Mentorship
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-655">
                Work alongside and learn from our team of Olympian head coaches
                — Donna Kellogg MBE, Natalie Barton and Sarah Chapman — gaining
                invaluable insights from elite-level experience.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-656">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-657">
                <TrendingUpIcon className="w-7 h-7" data-id="element-658" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-659">
                Competitive Pay
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-660">
                Earn well while making a difference in young athletes' lives.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-661">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-662">
                <HeartIcon className="w-7 h-7" data-id="element-663" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-664">
                Make a Difference
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-665">
                Play a direct role in building confidence, resilience and a love
                of sport in young people across Derbyshire, Hampshire and
                Hertfordshire.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-666">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-667">
                <CalendarIcon className="w-7 h-7" data-id="element-668" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-669">
                Flexible Opportunities
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-670">
                Choose from a range of coaching roles including after-school
                clubs, holiday camps, weekend sessions and school programme
                delivery.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Look For Section */}
      <section className="py-16 md:py-20 bg-white border-t border-gray-100" data-id="element-671">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-672">
          <div className="text-center mb-12" data-id="element-673">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-red uppercase mb-4" data-id="element-674">
              What We Look For
            </h2>
            <h3 className="text-xl md:text-2xl font-bold text-brand-blue uppercase" data-id="element-675">
              Qualities & Requirements
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-676">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-677">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-678">
                <ZapIcon className="w-7 h-7" data-id="element-679" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-680">
                Passion for Sport
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-681">
                A genuine enthusiasm for sport and physical activity, with a
                desire to share that passion with young people of all abilities.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-682">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-683">
                <AwardIcon className="w-7 h-7" data-id="element-684" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-685">
                Coaching Qualifications
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-686">
                Relevant coaching qualifications are preferred, but we also
                welcome enthusiastic individuals willing to train and develop.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-687">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-688">
                <ShieldCheckIcon className="w-7 h-7" data-id="element-689" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-690">
                DBS Checked
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-691">
                All coaches must hold or be willing to obtain an enhanced DBS
                check. Safeguarding training will be provided.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="bg-white p-8 rounded-2xl border border-navy-100 shadow-sm" data-id="element-692">
              <div className="w-14 h-14 rounded-xl bg-navy-50 text-brand-blue flex items-center justify-center mb-5" data-id="element-693">
                <UsersIcon className="w-7 h-7" data-id="element-694" />
              </div>
              <h4 className="text-lg font-bold text-navy-900 mb-3" data-id="element-695">
                Team Player
              </h4>
              <p className="text-navy-600 leading-relaxed text-base" data-id="element-696">
                The ability to work collaboratively within our coaching team,
                supporting fellow coaches and contributing to a positive
                environment.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Apply Section with Form */}
      <section id="apply" className="py-20 md:py-28 bg-navy-50 border-t border-gray-100" data-id="element-697">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-698">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start" data-id="element-699">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-700">
              <span className="block text-sm font-bold tracking-widest uppercase mb-3 text-gold-600" data-id="element-701">
                READY TO JOIN?
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-navy-900 mb-6" data-id="element-702">
                Start Your Coaching Journey
              </h2>
              <p className="text-lg text-navy-600 mb-8 leading-relaxed" data-id="element-703">
                Send us your details and tell us about your coaching experience.
                We'd love to welcome you to the Bright Star Sports team.
              </p>
              <div className="space-y-6" data-id="element-704">
                <div className="flex items-start" data-id="element-705">
                  <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-brand-blue" data-id="element-706">
                    <MedalIcon className="w-5 h-5" data-id="element-707" />
                  </div>
                  <div data-id="element-708">
                    <h4 className="font-bold text-navy-900" data-id="element-709">
                      Train With Olympians
                    </h4>
                    <p className="text-navy-600 text-sm mt-1" data-id="element-710">
                      Work alongside our team of Olympian head coaches.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-711">
                  <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-brand-blue" data-id="element-712">
                    <HeartIcon className="w-5 h-5" data-id="element-713" />
                  </div>
                  <div data-id="element-714">
                    <h4 className="font-bold text-navy-900" data-id="element-715">
                      Make a Real Impact
                    </h4>
                    <p className="text-navy-600 text-sm mt-1" data-id="element-716">
                      Help young athletes build confidence, skills and
                      friendships.
                    </p>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-717">
                  <div className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center mr-4 flex-shrink-0 text-brand-blue" data-id="element-718">
                    <CalendarIcon className="w-5 h-5" data-id="element-719" />
                  </div>
                  <div data-id="element-720">
                    <h4 className="font-bold text-navy-900" data-id="element-721">Flexible Roles</h4>
                    <p className="text-navy-600 text-sm mt-1" data-id="element-722">
                      After-school clubs, holiday camps, weekends and more.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right: Application Form */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.3,
            duration: 0.6
          }} data-id="element-723">
              <CoachApplicationForm data-id="element-724" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <ValuesSection ctaLabel="View Our Programmes" ctaHref="/book" data-id="element-725" />
    </main>;
}